'use client';

import { useTranslations } from 'next-intl';
import {
  User,
  ShoppingBag,
  MapPin,
  Heart,
  Star,
  Sparkles,
  FileText,
  Shield,
  MessageSquare,
  RotateCcw,
  LogOut,
} from 'lucide-react';
import { Link, usePathname } from '@/lib/i18n/navigation';
import { cn } from '@/lib/utils';

interface NavItem {
  href: string;
  labelKey:
    | 'navProfile'
    | 'navOrders'
    | 'navReturns'
    | 'navAddresses'
    | 'navWishlist'
    | 'navInvoices'
    | 'navPrivacy'
    | 'navNewsletter'
    | 'navSecurity';
  fallbackEs: string;
  fallbackEn: string;
  icon: typeof User;
  exact?: boolean;
}

const SUMMARY_FALLBACK = {
  href: '/cuenta',
  icon: User,
  exact: true,
};

const NAV: NavItem[] = [
  {
    href: '/cuenta/perfil',
    labelKey: 'navProfile',
    fallbackEs: 'Perfil',
    fallbackEn: 'Profile',
    icon: User,
  },
  {
    href: '/cuenta/pedidos',
    labelKey: 'navOrders',
    fallbackEs: 'Pedidos',
    fallbackEn: 'Orders',
    icon: ShoppingBag,
  },
  {
    href: '/cuenta/devoluciones',
    labelKey: 'navReturns',
    fallbackEs: 'Devoluciones',
    fallbackEn: 'Returns',
    icon: RotateCcw,
  },
  {
    href: '/cuenta/direcciones',
    labelKey: 'navAddresses',
    fallbackEs: 'Direcciones',
    fallbackEn: 'Addresses',
    icon: MapPin,
  },
  {
    href: '/cuenta/wishlist',
    labelKey: 'navWishlist',
    fallbackEs: 'Wishlist',
    fallbackEn: 'Wishlist',
    icon: Heart,
  },
  {
    href: '/cuenta/reviews',
    labelKey: 'navProfile',
    fallbackEs: 'Reviews',
    fallbackEn: 'Reviews',
    icon: Star,
  },
  {
    href: '/cuenta/tarot',
    labelKey: 'navProfile',
    fallbackEs: 'Tarot',
    fallbackEn: 'Tarot',
    icon: Sparkles,
  },
  {
    href: '/cuenta/facturas',
    labelKey: 'navInvoices',
    fallbackEs: 'Facturas CFDI',
    fallbackEn: 'CFDI invoices',
    icon: FileText,
  },
  {
    href: '/cuenta/privacidad',
    labelKey: 'navPrivacy',
    fallbackEs: 'Privacidad (ARCO)',
    fallbackEn: 'Privacy (ARCO)',
    icon: Shield,
  },
  {
    href: '/cuenta/soporte',
    labelKey: 'navProfile',
    fallbackEs: 'Soporte',
    fallbackEn: 'Support',
    icon: MessageSquare,
  },
];

export function AccountSidebar({ userName }: { userName: string | null | undefined }) {
  const t = useTranslations('account');
  const tHeader = useTranslations('header');
  const tNav = useTranslations('nav');
  const pathname = usePathname();

  return (
    <aside className="sticky top-24 w-full shrink-0 lg:w-[260px]">
      <div className="mb-6 border-b border-[var(--color-border)] pb-5">
        <p className="type-eyebrow text-[var(--color-fg-subtle)]">{tHeader('myAccount')}</p>
        <p className="type-h4 mt-1 text-[var(--color-fg)]">{userName?.split(' ')[0] ?? 'Φ'}</p>
      </div>
      <nav>
        <ul className="flex flex-col gap-1">
          <li>
            <Link
              href={SUMMARY_FALLBACK.href}
              className={cn(
                'type-nav flex items-center gap-3 rounded-sm px-3 py-2 transition-colors',
                pathname === SUMMARY_FALLBACK.href
                  ? 'bg-[var(--color-accent)]/10 text-[var(--color-accent)]'
                  : 'text-[var(--color-fg-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-fg)]'
              )}
            >
              <User className="h-4 w-4 shrink-0" strokeWidth={1.25} />
              {t('title')}
            </Link>
          </li>
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = pathname === item.href || pathname.startsWith(item.href + '/');
            return (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className={cn(
                    'type-nav flex items-center gap-3 rounded-sm px-3 py-2 transition-colors',
                    active
                      ? 'bg-[var(--color-accent)]/10 text-[var(--color-accent)]'
                      : 'text-[var(--color-fg-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-fg)]'
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" strokeWidth={1.25} />
                  {item.labelKey === 'navProfile' && item.fallbackEs !== 'Perfil'
                    ? item.fallbackEs // labels custom (Tarot, Reviews, Soporte) — usar fallback directo
                    : t(item.labelKey)}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      <form
        action="/api/auth/signout"
        method="POST"
        className="mt-6 border-t border-[var(--color-border)] pt-5"
      >
        <button
          type="submit"
          className="type-nav flex items-center gap-3 rounded-sm px-3 py-2 text-[var(--color-fg-subtle)] transition-colors hover:text-[var(--color-danger)]"
        >
          <LogOut className="h-4 w-4" strokeWidth={1.25} />
          {tNav('logout')}
        </button>
      </form>
    </aside>
  );
}
